import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ROOM_TYPES, FLOOR_MATERIALS, WALL_MATERIALS, selectRoom, goToPhase } from '../../store/houseSlice';
import { useT, Tx } from '../../i18n';
import { useAuth } from '../../context/AuthContext';
import { saveProjectToWeb } from '../../firebase/workspaceService';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';
import { useAutoSave } from '../../hooks/useAutoSave';
import { saveOrShareImage, drawLogoWatermark } from '../../utils/screenshot';
import { useGuestMode } from '../../context/GuestModeContext';
import GuestSaveButton from '../GuestSaveButton';
import './FloorPlan.css';
const FILE_VERSION = 1;

function downloadSvgAsPng(svgEl, filename) {
  const vb = svgEl.viewBox?.baseVal;
  const w = (vb?.width > 0 ? vb.width : null) || svgEl.getBoundingClientRect().width || 1200;
  const h = (vb?.height > 0 ? vb.height : null) || svgEl.getBoundingClientRect().height || 900;
  const clone = svgEl.cloneNode(true);
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(w));
  clone.setAttribute('height', String(h));
  const str = new XMLSerializer().serializeToString(clone);
  const blob = new Blob([str], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => {
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#f5f6fa';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    URL.revokeObjectURL(url);
    drawLogoWatermark(ctx, w, h);
    saveOrShareImage(c.toDataURL('image/png'), filename, 'Floor Plan');
  };
  img.onerror = () => URL.revokeObjectURL(url);
  img.src = url;
}

function resolveMat(val, matList) {
  if (!val) return null;
  if (typeof val === 'object' && val.__custom) return val;
  return matList.find(m => m.id === val) || null;
}

const CELL = 48;

// Step-wall openings store absX/absY in Builder/ThreeDRoom's shared 26px-per-
// grid-unit convention (see ThreeDRoom.jsx's CELL) — independent of this
// view's own render scale (CELL above), so convert via grid units.
const OPENING_ABS_CELL = 26;

// Same compound-outline function as Builder.jsx
// A free-form room carries a smooth outline (`pts`: angled/curved edges) beside
// its rasterised rects. Trace that so the plan shows the real shape, not a
// staircase of cells. Returns '' when the room isn't free-form.
function getFreePath(room, C) {
  const P = room?.pts;
  if (!P || P.length < 3) return '';
  const px = p => (p.x - room.x) * C, py = p => (p.y - room.y) * C;
  let d = `M ${px(P[0])} ${py(P[0])}`;
  for (let i = 0; i < P.length; i++) {
    const a = P[i], b = P[(i + 1) % P.length];
    if (a.bulge) {
      const dx = b.x - a.x, dy = b.y - a.y;
      const len = Math.hypot(dx, dy) || 1;
      const cx = (a.x + b.x) / 2 + (dy / len) * a.bulge * 2;
      const cy = (a.y + b.y) / 2 + (-dx / len) * a.bulge * 2;
      d += ` Q ${(cx - room.x) * C} ${(cy - room.y) * C} ${px(b)} ${py(b)}`;
    } else d += ` L ${px(b)} ${py(b)}`;
  }
  d += ' Z';
  // A merged free-form room also carries the straight rooms it absorbed — append
  // them as extra subpaths so the filled shape is their union.
  if (room.mergeRects?.length) {
    d += ' ' + getCompoundPath(room.mergeRects, C, room.x, room.y);
  }
  return d;
}

function getCompoundPath(rects, C, originX, originY) {
  if (!rects || rects.length === 0) return '';
  const cells = new Set();
  for (const r of rects)
    for (let row = r.y; row < r.y + r.h; row++)
      for (let col = r.x; col < r.x + r.w; col++)
        cells.add(`${col},${row}`);

  const segs = [];
  for (const key of cells) {
    const [col, row] = key.split(',').map(Number);
    const x = (col - originX) * C, y = (row - originY) * C;
    if (!cells.has(`${col},${row - 1}`)) segs.push([x,     y,     x + C, y    ]);
    if (!cells.has(`${col + 1},${row}`)) segs.push([x + C, y,     x + C, y + C]);
    if (!cells.has(`${col},${row + 1}`)) segs.push([x + C, y + C, x,     y + C]);
    if (!cells.has(`${col - 1},${row}`)) segs.push([x,     y + C, x,     y    ]);
  }
  const adj = {};
  segs.forEach((s, i) => { const k = `${s[0]},${s[1]}`; (adj[k] = adj[k] || []).push(i); });
  const used = new Array(segs.length).fill(false);
  const paths = [];
  for (let si = 0; si < segs.length; si++) {
    if (used[si]) continue;
    const pts = [];
    let cur = si;
    for (let guard = 0; guard < segs.length + 4; guard++) {
      if (used[cur]) break;
      used[cur] = true;
      pts.push(`${segs[cur][0]} ${segs[cur][1]}`);
      const k = `${segs[cur][2]},${segs[cur][3]}`;
      const nexts = (adj[k] || []).filter(i => !used[i]);
      if (!nexts.length) break;
      cur = nexts[0];
    }
    if (pts.length > 1) paths.push(`M ${pts.join(' L ')} Z`);
  }
  return paths.join(' ');
}

function shapeCentroid(rects, C, originX, originY) {
  let totalArea = 0, cx = 0, cy = 0;
  for (const r of rects) {
    const area = r.w * r.h;
    cx += (r.x + r.w / 2 - originX) * area;
    cy += (r.y + r.h / 2 - originY) * area;
    totalArea += area;
  }
  return totalArea > 0
    ? { x: (cx / totalArea) * C, y: (cy / totalArea) * C }
    : { x: (rects[0].w / 2) * C, y: (rects[0].h / 2) * C };
}

function FloorPlanOpening({ opening, rx, ry, rw, rh }) {
  const { type, wall, pos, absX, absY } = opening;
  const defW = type === 'door' ? 0.88 : type === 'archway' ? 1.2 : 0.98;
  const sw = (opening.width || defW) * CELL;
  const dc = opening.color || (type === 'door' ? '#c4a87a' : type === 'archway' ? '#e0d8c8' : '#3a78b0');
  let t;
  if (absX !== undefined && absY !== undefined) {
    // Step-wall opening (compound room) — precise position, not a bbox fraction
    const px = rx + (absX / OPENING_ABS_CELL) * CELL;
    const py = ry + (absY / OPENING_ABS_CELL) * CELL;
    if (wall === 'top')         t = `translate(${px},${py})`;
    else if (wall === 'bottom') t = `translate(${px},${py}) rotate(180)`;
    else if (wall === 'left')   t = `translate(${px},${py}) rotate(-90)`;
    else                        t = `translate(${px},${py}) rotate(90)`;
  } else {
    const cxOff = (wall === 'top' || wall === 'bottom') ? pos * rw : 0;
    const cyOff = (wall === 'left' || wall === 'right') ? pos * rh : 0;
    if (wall === 'top')         t = `translate(${rx + cxOff},${ry})`;
    else if (wall === 'bottom') t = `translate(${rx + cxOff},${ry + rh}) rotate(180)`;
    else if (wall === 'left')   t = `translate(${rx},${ry + cyOff}) rotate(-90)`;
    else                        t = `translate(${rx + rw},${ry + cyOff}) rotate(90)`;
  }
  return (
    <g transform={t}>
      <rect x={-sw / 2} y={-6} width={sw} height={12} fill="white" opacity="0.92"/>
      {type === 'door' ? (<>
        <line x1={sw / 2} y1={0} x2={sw / 2} y2={sw} stroke={dc} strokeWidth={1.5}/>
        <path d={`M${sw/2},${sw} A${sw},${sw} 0 0,0 ${-sw/2},0`} fill={dc + '40'} stroke={dc} strokeWidth={1}/>
      </>) : type === 'archway' ? (
        <path d={`M${-sw/2},${sw*0.45} A${sw/2},${sw/2} 0 0,1 ${sw/2},${sw*0.45}`} fill={dc + '30'} stroke={dc} strokeWidth={1.5}/>
      ) : (<>
        <rect x={-sw / 2} y={-3.5} width={sw} height={7} fill="none" stroke={dc} strokeWidth={1.5}/>
        <line x1={-sw / 2} y1={0} x2={sw / 2} y2={0} stroke={dc} strokeWidth={1.5}/>
      </>)}
    </g>
  );
}

function FloorPlanAutoWallOpening({ op, cx, cy, isH }) {
  const sw = (op.width || (op.type === 'door' ? 0.88 : op.type === 'archway' ? 1.2 : 0.98)) * CELL;
  const dc = op.color || (op.type === 'door' ? '#c4a87a' : op.type === 'archway' ? '#e0d8c8' : '#3a78b0');
  return (
    <g transform={`translate(${cx},${cy}) rotate(${isH ? 0 : 90})`}>
      <rect x={-sw / 2} y={-6} width={sw} height={12} fill="white" opacity="0.92"/>
      {op.type === 'door' ? (<>
        <line x1={sw / 2} y1={0} x2={sw / 2} y2={sw} stroke={dc} strokeWidth={1.5}/>
        <path d={`M${sw/2},${sw} A${sw},${sw} 0 0,0 ${-sw/2},0`} fill={dc + '40'} stroke={dc} strokeWidth={1}/>
      </>) : op.type === 'archway' ? (
        <path d={`M${-sw/2},${sw*0.45} A${sw/2},${sw/2} 0 0,1 ${sw/2},${sw*0.45}`} fill={dc + '30'} stroke={dc} strokeWidth={1.5}/>
      ) : (<>
        <rect x={-sw / 2} y={-3.5} width={sw} height={7} fill="none" stroke={dc} strokeWidth={1.5}/>
        <line x1={-sw / 2} y1={0} x2={sw / 2} y2={0} stroke={dc} strokeWidth={1.5}/>
      </>)}
    </g>
  );
}

export default function FloorPlan() {
  const dispatch = useDispatch();
  const { rooms, designs, autoWallOpenings } = useSelector(s => s.house);
  const houseState = useSelector(s => s.house);
  const svgRef = useRef(null);
  const { t } = useT();
  const { user } = useAuth();
  const { projectId } = useParams();
  const isGuest = useGuestMode();
  const { markSaved, isDirty, changeToken, confirmIfDirty } = useUnsavedChanges();
  const [webSaving, setWebSaving] = useState(false);

  async function handleWebSave() {
    if (!projectId || !user) return;
    setWebSaving(true);
    const s = houseState;
    const sceneData = JSON.stringify({
      version: FILE_VERSION, savedAt: new Date().toISOString(),
      phase: s.phase, rooms: s.rooms, designs: s.designs, furniture: s.furniture, lighting: s.lighting || [],
      wallSegments: s.wallSegments || [], freeWalls: s.freeWalls || [], nextFreeWallId: s.nextFreeWallId || 1,
      freeRooms: s.freeRooms || [], nextFreeRoomId: s.nextFreeRoomId || 1,
      autoWallOpenings: s.autoWallOpenings || {},
      wallHeight: s.wallHeight ?? 2.8, nextId: s.nextId,
      nextFurnitureId: s.nextFurnitureId, nextLightingId: s.nextLightingId || 1, nextOpeningId: s.nextOpeningId,
      nextWallId: s.nextWallId || 1, nextWallOpeningId: s.nextWallOpeningId || 1,
    });
    try {
      await saveProjectToWeb(projectId, sceneData);
      markSaved();
    } catch (err) {
      console.error('Web save failed:', err);
    } finally {
      setWebSaving(false);
    }
  }

  // Autosave to the cloud shortly after the user stops editing.
  useAutoSave({
    isDirty, changeToken, save: handleWebSave,
    enabled: !!projectId && !!user,
  });

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  rooms.forEach(r => {
    minX = Math.min(minX, r.x); minY = Math.min(minY, r.y);
    maxX = Math.max(maxX, r.x + r.w); maxY = Math.max(maxY, r.y + r.h);
  });
  const pad = 2;
  const svgW = (maxX - minX + pad * 2) * CELL;
  const svgH = (maxY - minY + pad * 2) * CELL;
  const ox = (pad - minX) * CELL;
  const oy = (pad - minY) * CELL;

  return (
    <div className="floorplan-page">
      <div className="floorplan-header">
        <button className="fp-back-btn" onClick={() => confirmIfDirty(() => dispatch(goToPhase('build')))}>
          <Tx k="edit_layout" />
        </button>
        <div className="fp-title-block">
          <span className="fp-eyebrow">{t('step2')}</span>
          <h1>{t('floor_plan_title')}</h1>
          <p>{t('floor_plan_sub')}</p>
        </div>
        <div className="fp-stats">
          {/* The room / styled counters and the "Saved" chip used to live here.
              Both were READOUTS, not controls: the counts are visible in the
              plan itself and the save state is handled by autosave, so on a
              phone they were spending the header's width saying what the user
              could already see. The guest save button stays — that one is a
              real action, and a guest has no autosave behind it. */}
          {isGuest && <GuestSaveButton />}
          {!isGuest && (
            <button
              className="fp-3d-btn"
              title="Screenshot"
              onClick={() => svgRef.current && downloadSvgAsPng(svgRef.current, 'floor-plan.png')}
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', boxShadow: 'none', padding: '9px 14px' }}
            >
              <svg width="15" height="15" viewBox="0 0 14 14" fill="none">
                <path d="M1 4.5C1 3.67 1.67 3 2.5 3H4L5 1.5H9L10 3H11.5C12.33 3 13 3.67 13 4.5V10.5C13 11.33 12.33 12 11.5 12H2.5C1.67 12 1 11.33 1 10.5V4.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
                <circle cx="7" cy="7.5" r="2" stroke="currentColor" strokeWidth="1.2"/>
              </svg>
            </button>
          )}
          <button
            className="fp-3d-btn"
            onClick={() => confirmIfDirty(() => dispatch(goToPhase('view3d')))}
            disabled={rooms.length === 0}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 1L13 4.5V9.5L7 13L1 9.5V4.5L7 1Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
              <path d="M7 1V13M1 4.5L7 8L13 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
            </svg>
            <Tx k="view_3d" />
          </button>
        </div>
      </div>

      <div className="floorplan-canvas-area">
        {rooms.length > 0 && (
          <div className="fp-click-hint"><Tx k="fp_click_room_hint" /></div>
        )}
        <div className="floorplan-svg-wrap">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${svgW} ${svgH}`}
          className="floorplan-svg"
        >
          <defs>
            <pattern id="hatch" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
              <path d="M-1,1 l2,-2 M0,8 l8,-8 M7,9 l2,-2" stroke="#bbbec8" strokeWidth="0.6" opacity="0.35"/>
            </pattern>
            {/* Per-room clip paths for compound shapes */}
            {rooms.map(room => {
              const rects = (room.pts && room.pts.length >= 3) ? (room.rects || []) : (room.rects && room.rects.length > 1 ? room.rects : null);
              if (!rects) return null;
              const rx = room.x * CELL + ox;
              const ry = room.y * CELL + oy;
              const cPath = getFreePath(room, CELL) || getCompoundPath(rects, CELL, room.x, room.y);
              return (
                <clipPath key={`clip-${room.id}`} id={`clip-${room.id}`}>
                  <path d={cPath} transform={`translate(${rx},${ry})`}/>
                </clipPath>
              );
            })}
          </defs>

          {/* Wall outer shell — shape-accurate */}
          {rooms.map(room => {
            const rects = (room.pts && room.pts.length >= 3) ? (room.rects || []) : (room.rects && room.rects.length > 1 ? room.rects : null);
            const rx = room.x * CELL + ox;
            const ry = room.y * CELL + oy;
            const rw = room.w * CELL;
            const rh = room.h * CELL;
            if (rects) {
              const cPath = getFreePath(room, CELL) || getCompoundPath(rects, CELL, room.x, room.y);
              return (
                <path key={`wall-${room.id}`}
                  d={cPath}
                  transform={`translate(${rx},${ry})`}
                  fill="#9ea5b8"
                  stroke="#9ea5b8"
                  strokeWidth={10}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              );
            }
            return (
              <rect key={`wall-${room.id}`}
                x={rx - 5} y={ry - 5} width={rw + 10} height={rh + 10}
                fill="#9ea5b8" rx="3"
              />
            );
          })}

          {/* Room fills + wall strips + border + labels */}
          {rooms.map(room => {
            const rt = ROOM_TYPES.find(r => r.type === room.type);
            const design = designs[room.id];
            const isStyled = design && (design.floor || design.wall || design.ceiling);
            const floorMat = resolveMat(design?.floor, FLOOR_MATERIALS);
            const wallMat  = resolveMat(design?.wall,  WALL_MATERIALS);

            const rx = room.x * CELL + ox;
            const ry = room.y * CELL + oy;
            const rw = room.w * CELL;
            const rh = room.h * CELL;

            const rects    = room.rects && room.rects.length > 1 ? room.rects : null;
            const cPath    = getFreePath(room, CELL) || (rects ? getCompoundPath(rects, CELL, room.x, room.y) : null);
            const centroid = rects ? shapeCentroid(rects, CELL, room.x, room.y) : { x: rw / 2, y: rh / 2 };
            const clipId   = rects ? `url(#clip-${room.id})` : undefined;

            const floorFill = floorMat ? floorMat.baseColor : 'url(#hatch)';
            const borderColor = isStyled ? '#d4903a' : '#b0b5c5';
            const borderW  = isStyled ? 2 : 1.5;

            // Label anchor (centroid in SVG space)
            const lx = rx + centroid.x;
            const ly = ry + centroid.y;

            return (
              <g
                key={room.id}
                className="fp-room-group"
                onClick={() => dispatch(selectRoom(room.id))}
                role="button"
                aria-label={`Design ${room.label}`}
              >
                {/* Floor fill — compound: use clipPath */}
                {rects ? (
                  <path d={cPath} transform={`translate(${rx},${ry})`} fill={floorFill}/>
                ) : (
                  <rect x={rx} y={ry} width={rw} height={rh} fill={floorFill}/>
                )}

                {/* Wall strips (clipped to actual shape for compound rooms) */}
                {wallMat && (
                  <g clipPath={clipId}>
                    <rect x={rx} y={ry}              width={rw} height={8} fill={wallMat.color} opacity="0.85"/>
                    <rect x={rx} y={ry + rh - 8}     width={rw} height={8} fill={wallMat.color} opacity="0.65"/>
                    <rect x={rx} y={ry}              width={8}  height={rh} fill={wallMat.color} opacity="0.75"/>
                    <rect x={rx + rw - 8} y={ry}     width={8}  height={rh} fill={wallMat.color} opacity="0.55"/>
                  </g>
                )}

                {/* Border */}
                {rects ? (
                  <path d={cPath} transform={`translate(${rx},${ry})`}
                    fill="none" stroke={borderColor} strokeWidth={borderW} strokeLinejoin="round"/>
                ) : (
                  <rect x={rx} y={ry} width={rw} height={rh}
                    fill="none" stroke={borderColor} strokeWidth={borderW}/>
                )}

                {/* Hover overlay (clipped for compound) */}
                {rects ? (
                  <path d={cPath} transform={`translate(${rx},${ry})`}
                    fill="rgba(184,147,90,0)" className="fp-hover-overlay"/>
                ) : (
                  <rect x={rx} y={ry} width={rw} height={rh}
                    fill="rgba(184,147,90,0)" className="fp-hover-overlay"/>
                )}

                {/* Labels at centroid */}
                <text x={lx} y={ly - 14}
                  textAnchor="middle" fontSize="20" className="fp-room-icon">
                  {rt?.icon}
                </text>
                <text x={lx} y={ly + 4}
                  textAnchor="middle" fontSize="10" fontFamily="DM Sans, sans-serif"
                  fontWeight="600" fill={isStyled ? '#7a4a10' : '#6b6e82'}
                  letterSpacing="0.04em">
                  {room.label.toUpperCase()}
                </text>
                {isStyled && (
                  <text x={lx} y={ly + 18}
                    textAnchor="middle" fontSize="9" fontFamily="DM Sans, sans-serif"
                    fill="#b8935a" fontWeight="500">
                    ✦ styled
                  </text>
                )}
                <text x={lx} y={ly + (isStyled ? 30 : 20)}
                  textAnchor="middle" fontSize="8" fontFamily="DM Mono, monospace"
                  fill="#9598ac">
                  {room.w}m × {room.h}m
                </text>
              </g>
            );
          })}

          {/* Room openings (room.openings) */}
          {rooms.flatMap(room => {
            const rx = room.x * CELL + ox;
            const ry = room.y * CELL + oy;
            const rw = room.w * CELL;
            const rh = room.h * CELL;
            return (room.openings || []).map(op => (
              <FloorPlanOpening key={`rop-${room.id}-${op.id}`} opening={op} rx={rx} ry={ry} rw={rw} rh={rh} />
            ));
          })}

          {/* Auto-wall openings (autoWallOpenings) */}
          {Object.entries(autoWallOpenings || {}).flatMap(([key, ops]) => {
            if (!ops || !ops.length) return [];
            const parts = key.split(':');
            if (parts[0] === 'h') {
              const Y = Number(parts[1]), X1 = Number(parts[2]), X2 = Number(parts[3]);
              const autoLen = X2 - X1;
              return ops.map(op => {
                const worldX = X1 + op.t * autoLen;
                return <FloorPlanAutoWallOpening key={`awop-${key}-${op.id}`} op={op}
                  cx={worldX * CELL + ox} cy={Y * CELL + oy} isH={true} />;
              });
            }
            if (parts[0] === 'v') {
              const X = Number(parts[1]), Y1 = Number(parts[2]), Y2 = Number(parts[3]);
              const autoLen = Y2 - Y1;
              return ops.map(op => {
                const worldY = Y1 + op.t * autoLen;
                return <FloorPlanAutoWallOpening key={`awop-${key}-${op.id}`} op={op}
                  cx={X * CELL + ox} cy={worldY * CELL + oy} isH={false} />;
              });
            }
            return [];
          })}

          {/* Compass */}
          <g transform={`translate(${svgW - 30}, ${svgH - 30})`}>
            <circle cx="0" cy="0" r="18" fill="#1e2235" opacity="0.88"/>
            <text x="0" y="-5" textAnchor="middle" fontSize="8"
              fill="#d4903a" fontFamily="DM Sans" fontWeight="700">N</text>
            <polygon points="0,-12 -3,-3 0,-6 3,-3" fill="#d4903a"/>
            <polygon points="0,12 -3,3 0,6 3,3" fill="#505468"/>
          </g>
        </svg>
        </div>

        <div className="fp-legend">
          <div className="fp-legend-item">
            <span className="fp-legend-swatch" style={{background:'#c8cbd8', border:'1.5px solid #b0b5c5'}}/>
            <span>{t('unstyled')}</span>
          </div>
          <div className="fp-legend-item">
            <span className="fp-legend-swatch" style={{background:'#e8a850', border:'2px solid #d4903a'}}/>
            <span>{t('styled_stat')}</span>
          </div>
          <div className="fp-legend-item">
            <span className="fp-legend-swatch" style={{background:'#9ea5b8'}}/>
            <span>{t('walls')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
